import React from 'react';
import { Box, Typography } from '@mui/material';
import homepage_bg_red from '../../assets/homepage_bg_red.png';
import flight_icon_homepage from '../../assets/flight_icon_homepage.svg';
import train_icon_homepage from '../../assets/Train_icon_homepage.svg';

const FlightOneWayRoundTripSearchBar = ({ tripType, multiCitySegments, icon }) => {
  // Calculate dynamic height to be half of form height across all screen sizes
  // Form heights estimated: xs: ~180px, sm: ~200px, md: ~240px, lg: ~280px, xl: ~300px
  // Red bg height = (form height / 2) + 50px overlap = half form height
  const getDynamicHeight = () => {
    if (tripType === 'multicity' && multiCitySegments) {
      // Base height is half of estimated form height + overlap
      const baseHeight = { xs: 180, sm: 180, md: 210, lg: 230, xl: 240 }; // Increased from bottom
      // For multicity, form grows by ~60px per segment, so red bg grows by half
      const additionalHeight = multiCitySegments.length * 30; // 30px per segment (half of 60px)
      return {
        xs: `${baseHeight.xs + additionalHeight}px`,
        sm: `${baseHeight.sm + additionalHeight}px`,
        md: `${baseHeight.md + additionalHeight}px`,
        lg: `${baseHeight.lg + additionalHeight}px`,
        xl: `${baseHeight.xl + additionalHeight}px`
      };
    }
    // Standard height: half of form height + 50px overlap to maintain consistent ratio
    // Increased from bottom to add more red background space
    return { 
      xs: '180px',    // Mobile: increased from 140px
      sm: '180px',    // Small: matches mobile
      md: '210px',    // Medium: increased from 170px
      lg: '230px',    // Large: increased from 190px
      xl: '240px'     // Extra large: increased from 200px
    };
  };

  // Calculate content top position to maintain consistent positioning
  const getContentTopPosition = () => {
    // Keep content position fixed regardless of trip type
    return { xs: '20px', sm: '20px', md: '30px' };
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: { xs: 'none', sm: 'none', md: 'flex' },
        justifyContent: 'center',
        mt: 0.5,
        mb: '25px', // Bottom margin to account for form overlap with negative margin
        // px: '24px' // Fixed horizontal padding - same at all screen sizes to maintain consistent gap
        paddingLeft: { 
          xs: '16px', 
          sm: '16px', 
          md: 'clamp(20px, 1.25vw, 40px)',
          lg: 'clamp(24px, 1.25vw, 48px)'
        },
        paddingRight: { 
          xs: '16px', 
          sm: '16px', 
          md: 'clamp(20px, 1.25vw, 40px)',
          lg: 'clamp(24px, 1.25vw, 48px)'
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: {
            xs: '100%',     // Mobile: full width
            sm: '100%',     // Small: full width
            md: '1200px',   // Medium: 1200px - matches header
            lg: '1400px',   // Large: 1400px - matches header
            xl: '1600px'    // Extra large: 1600px - matches header
          },
          minHeight: getDynamicHeight(),
          transition: 'min-height 0.3s ease-in-out',
          backgroundImage: `url(${homepage_bg_red})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          pt: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2.5 }, // Top padding
          pb: { xs: 3, sm: 3, md: 4, lg: 4, xl: 4.5 }, // Increased bottom padding to prevent clipping when form overlaps
          overflow: 'visible',
          borderRadius: 4,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0',
          mx: 'auto' // Center the box to match header alignment
        }}
      >
        
        {/* Main Content - Fixed Position */}
        <Box sx={{
          position: 'absolute',
          top: getContentTopPosition(),
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          textAlign: 'center',
          gap: '8px', // Fixed gap - same at all screen sizes
          width: '100%',
          maxWidth: '100%'
        }}>
          {/* Subtitle */}
             {/* Main Title with Icon */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.8, sm: 0.8, md: 1.5 },
            flexWrap: 'nowrap',
            maxWidth: '100%',
            mb: '8px' // Fixed margin bottom - reduced gap between text and form
          }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '0.9rem', sm: '0.9rem', md: '1.45rem' },
                color: '#fff',
                lineHeight: '36px',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                letterSpacing: '0.2px',
                whiteSpace: 'nowrap',
                flexShrink: 1,
                minWidth: 0
              }}
            >
              Effortless booking for flights, trains & stays, powered by AI
            </Typography>
            
            {/* Icon - Flight, Train, or Hotel */}
            {icon === 'hotel' ? (
              <Box
                component="svg"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                sx={{
                  width: { xs: '1.2rem', sm: '1.2rem', md: '2.2rem' },
                  height: { xs: '1.2rem', sm: '1.2rem', md: '2.2rem' },
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                  flexShrink: 0,
                  '& path': {
                    fill: '#fff'
                  }
                }}
              >
                <path d="M20.25 10.8141C19.7772 10.6065 19.2664 10.4996 18.75 10.5H5.25C4.73368 10.4995 4.22288 10.6063 3.75 10.8136C3.08166 11.1059 2.51294 11.5865 2.11336 12.1968C1.71377 12.8071 1.50064 13.5205 1.5 14.25V19.5C1.5 19.6989 1.57902 19.8897 1.71967 20.0303C1.86032 20.171 2.05109 20.25 2.25 20.25C2.44891 20.25 2.63968 20.171 2.78033 20.0303C2.92098 19.8897 3 19.6989 3 19.5V19.125C3.00122 19.0259 3.04112 18.9312 3.11118 18.8612C3.18124 18.7911 3.27592 18.7512 3.375 18.75H20.625C20.7241 18.7512 20.8188 18.7911 20.8888 18.8612C20.9589 18.9312 20.9988 19.0259 21 19.125V19.5C21 19.6989 21.079 19.8897 21.2197 20.0303C21.3603 20.171 21.5511 20.25 21.75 20.25C21.9489 20.25 22.1397 20.171 22.2803 20.0303C22.421 19.8897 22.5 19.6989 22.5 19.5V14.25C22.4993 13.5206 22.2861 12.8073 21.8865 12.1971C21.4869 11.5869 20.9183 11.1063 20.25 10.8141ZM17.625 3.75H6.375C5.67881 3.75 5.01113 4.02656 4.51884 4.51884C4.02656 5.01113 3.75 5.67881 3.75 6.375V9.75C3.75002 9.77906 3.75679 9.80771 3.76979 9.8337C3.78278 9.85969 3.80163 9.8823 3.82486 9.89976C3.84809 9.91721 3.87505 9.92903 3.90363 9.93428C3.93221 9.93953 3.96162 9.93806 3.98953 9.93C4.39896 9.81025 4.82341 9.74964 5.25 9.75H5.44828C5.49456 9.75029 5.53932 9.73346 5.57393 9.70274C5.60855 9.67202 5.63058 9.62958 5.63578 9.58359C5.67669 9.21712 5.85115 8.87856 6.12586 8.63256C6.40056 8.38656 6.75625 8.25037 7.125 8.25H9.75C10.119 8.25003 10.475 8.38606 10.75 8.63209C11.025 8.87812 11.1997 9.21688 11.2406 9.58359C11.2458 9.62958 11.2679 9.67202 11.3025 9.70274C11.3371 9.73346 11.3818 9.75029 11.4281 9.75H12.5747C12.621 9.75029 12.6657 9.73346 12.7003 9.70274C12.735 9.67202 12.757 9.62958 12.7622 9.58359C12.8031 9.21736 12.9773 8.87899 13.2517 8.63303C13.5261 8.38706 13.8815 8.25072 14.25 8.25H16.875C17.244 8.25003 17.6 8.38606 17.875 8.63209C18.15 8.87812 18.3247 9.21688 18.3656 9.58359C18.3708 9.62958 18.3929 9.67202 18.4275 9.70274C18.4621 9.73346 18.5068 9.75029 18.5531 9.75H18.75C19.1766 9.74979 19.6011 9.81057 20.0105 9.93047C20.0384 9.93854 20.0679 9.94 20.0965 9.93473C20.1251 9.92945 20.1521 9.91759 20.1753 9.90009C20.1986 9.88258 20.2174 9.8599 20.2304 9.83385C20.2433 9.8078 20.2501 9.7791 20.25 9.75V6.375C20.25 5.67881 19.9734 5.01113 19.4812 4.51884C18.9889 4.02656 18.3212 3.75 17.625 3.75Z" fill="#fff"/>
              </Box>
            ) : (
              <Box
                component="img"
                src={icon === 'flight' ? flight_icon_homepage : train_icon_homepage}
                alt={icon === 'flight' ? 'Flight Icon' : 'Train Icon'}
                sx={{
                  width: { xs: '1.2rem', sm: '1.2rem', md: '2.2rem' },
                  height: { xs: '1.2rem', sm: '1.2rem', md: '2.2rem' },
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                  transform: 'rotate(0deg)',
                  flexShrink: 0
                }}
              />
            )}
          </Box>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 400,
              fontStyle: 'normal',
              fontFamily: 'Host Grotesk',
              fontSize: { xs: '0.6rem', sm: '0.6rem', md: '14px' },
              color: '#fff',
              lineHeight:'24px',
              opacity: 0.95,
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              letterSpacing: '0.3px'
            }}
          >
            Intelligent travel planning with optimized routes and clear fares
          </Typography>
          
       
        </Box>
      </Box>
    </Box>
  );
};

export default FlightOneWayRoundTripSearchBar;
